import { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { ExclamationCircleIcon, XIcon } from '@heroicons/react/outline'
import { Button } from '../../../../components/Button'
import { ERROR_MODAL, MESSAGES } from '../../constants/messages'

export const CsvErrorModal = ({ isOpen, onClose, error }) => {
  if (!error) return null

  // Extrair o título principal do erro
  const getErrorTitle = (errorMessage) => {
    const lines = errorMessage.split('\n')
    const titleLine = lines[0] || errorMessage
    return titleLine.replace(/^[❌⚠️🔍📋💡]\s*/, '').trim()
  }

  // Extrair lista de SKUs problemáticos
  const getProblematicSkus = (errorMessage) => {
    const skuLines = []
    const lines = errorMessage.split('\n')
    
    for (const line of lines) {
      if (line.trim().startsWith('• ')) {
        const sku = line.trim().replace('• ', '')
        skuLines.push(sku)
      }
    }
    
    return skuLines
  }

  // Extrair dica/solução
  const getErrorTip = (errorMessage) => {
    const tipMatch = errorMessage.match(/💡:\s*(.+?)$/s);
    return tipMatch ? tipMatch[1].trim() : null;
  }

  const errorTitle = getErrorTitle(error)
  const problematicSkus = getProblematicSkus(error)
  const errorTip = getErrorTip(error)

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex justify-between items-start mb-6">
                  <div className="flex items-center">
                    <ExclamationCircleIcon className="h-8 w-8 text-red-500 mr-3 flex-shrink-0" />
                    <Dialog.Title as="h3" className="text-xl font-semibold leading-6 text-gray-900 mb-0">
                      {ERROR_MODAL.title}
                    </Dialog.Title>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <XIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Título destacado do erro */}
                  <div className="bg-red-50 border-l-4 border-red-400 p-4">
                    <h4 className="text-lg font-semibold text-red-800 mb-0">
                      {errorTitle}
                    </h4>
                  </div>

                  {/* Lista de SKUs problemáticos */}
                  {problematicSkus.length > 0 && (
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 mb-3">
                        SKUs com problema:
                      </h5>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <ul className="space-y-2 mb-0">
                          {problematicSkus.map((sku, index) => (
                            <li key={index} className="flex items-center text-sm">
                              <span className="inline-block w-2 h-2 bg-gray-400 rounded-full mr-3"></span>
                              <span className="font-mono text-gray-800">{sku}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Dica/Solução se existir */}
                  {errorTip && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <span className="text-yellow-500 mr-2 text-lg flex-shrink-0">💡</span>
                        <p className="text-yellow-800 leading-relaxed mb-0 text-sm">
                          {errorTip}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-6 flex justify-end">
                  <Button
                    onClick={onClose}
                    className="bg-gray-500 hover:bg-gray-600 text-white"
                  >
                    {MESSAGES.BUTTONS.close}
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}